<template>
  <div class="guard-code-editor">
    <div
      ref="editorContainer"
      class="guard-code-editor__container" />
  </div>
</template>

<script setup>
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import 'monaco-editor/esm/vs/language/typescript/monaco.contribution';
import {
  ref, onMounted, onBeforeUnmount, watch,
} from 'vue';

const GUARD_CONTEXT_TYPES = `
declare type TGuardPoint = { X: number; Y: number };
/** Текст замечания задаётся не здесь, а полем «Текст замечания» в бэкофисе.
 *  Значения из details подставляются в него по имени: {area}, {x}, {y}. */
declare type TGuardIssue = {
  code: string;
  severity?: 'info' | 'warning' | 'error';
  locator?: object;
  details?: Record<string, string | number | boolean | null>;
  fix?: object | null;
};
declare type TGuardPlanApi = {
  planId(): string | null;
  crmPlanId(): string | null;
  typeOfJson(): string;
  meta(): any;
  workspace(): { X: number; Y: number; Width: number; Height: number };
  contentBounds(): any;
  floors(): any[];
  currentFloorIndex(): number;
  rooms(floor?: any): any[];
  columns(floor?: any): any[];
  walls(floor?: any): any[];
  vertices(): any[];
  furniture(scope?: any): any[];
  furnitureGroups(floor?: any): any[];
  apertures(floor?: any): any[];
  cameraPoints(floor?: any): any[];
  decors(floor?: any): any[];
  portals(floor?: any): any[];
  roomById(id: string): any;
  furnitureById(id: string): any;
  roomOf(node: any): any;
  floorOf(node: any): any;
  roomAt(point: TGuardPoint, floor?: any): any;
  isSun(node: any): boolean;
  isColumn(room: any): boolean;
  kindOf(node: any): string;
  raw(): any;
};
declare type TGuardGeometryApi = {
  position(node: any): TGuardPoint | null;
  footprint(node: any): TGuardPoint[];
  roomContour(room: any): TGuardPoint[];
  boundsOf(node: any): any;
  containsPoint(area: any, point: TGuardPoint): boolean;
  pointInPolygon(polygon: TGuardPoint[], point: TGuardPoint): boolean;
  distance(from: TGuardPoint, to: TGuardPoint): number;
};
/** Читается внутри тела проверки. Возвращать нужно массив находок. */
declare const context: {
  readonly apiVersion: number;
  readonly trigger: 'save' | 'manual' | 'change';
  readonly scope: 'allFloors' | 'currentFloor';
  readonly settings: Record<string, string | number | boolean | null | (string | number | boolean | null)[]>;
  readonly plan: TGuardPlanApi;
  readonly geometry: TGuardGeometryApi;
  readonly fix: {
    replaceAll(nodes: object[], model: string, field?: string): object | null;
    provide(room: object, items: (string | string[])[]): object | null;
    floorHeight(floor: object, expected: number): object | null;
    roomType(room: object, expected: string): object | null;
    objects(nodes: object[], replacements?: string[]): object | null;
    place(room: object, model: string, options: object): object | null;
  };
  locate(node: object): object;
  log(message: string): void;
};
`;

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:hasErrors']);

const MODEL_URI = monaco.Uri.parse('inmemory://guards/guard-code.js');

const editorContainer = ref(null);
let editorInstance = null;
let model = null;
let disposeMarkers = null;
let disposeContent = null;

const reuseModel = (value) => {
  const existing = monaco.editor.getModel(MODEL_URI);
  if (!existing) return monaco.editor.createModel(value, 'javascript', MODEL_URI);

  if (existing.getValue() !== value) existing.setValue(value);
  return existing;
};

const emitHasErrors = () => {
  const markers = monaco.editor.getModelMarkers({ resource: MODEL_URI });
  emit('update:hasErrors', markers.some((marker) => marker.severity === monaco.MarkerSeverity.Error));
};

onMounted(() => {
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
    // Код проверки — тело функции, а Monaco разбирает его как отдельный скрипт,
    // поэтому обязательный return на верхнем уровне иначе считается ошибкой (TS1108).
    diagnosticCodesToIgnore: [1108],
  });
  monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
    allowNonTsExtensions: true,
    checkJs: false,
    lib: ['es2017'],
  });
  monaco.languages.typescript.javascriptDefaults.addExtraLib(GUARD_CONTEXT_TYPES, 'guards-api.d.ts');

  model = reuseModel(props.modelValue ?? '');

  editorInstance = monaco.editor.create(editorContainer.value, {
    model,
    theme: 'vs-dark',
    automaticLayout: true,
    minimap: { enabled: false },
    renderLineHighlight: 'none',
    readOnly: props.readOnly,
    scrollBeyondLastLine: false,
  });

  disposeContent = model.onDidChangeContent(() => emit('update:modelValue', model.getValue()));

  const uriString = MODEL_URI.toString();
  disposeMarkers = monaco.editor.onDidChangeMarkers((uris) => {
    if (uris.some((uri) => uri.toString() === uriString)) emitHasErrors();
  });

  emitHasErrors();
});

watch(() => props.modelValue, (value) => {
  if (model && value !== model.getValue()) model.setValue(value ?? '');
});

const layout = () => editorInstance?.layout();

defineExpose({ layout });

onBeforeUnmount(() => {
  disposeContent?.dispose();
  disposeMarkers?.dispose();
  editorInstance?.dispose();
  model = null;
});
</script>

<style scoped>
.guard-code-editor__container {
  width: 100%;
  height: 420px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
}
</style>
